import { View, Text, StyleSheet, Platform } from 'react-native';
import WebScrollView from '../components/WebScrollView';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <View style={styles.container}>
      <WebScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          <View style={styles.header}>
            <Text style={styles.title}>Privacy Policy</Text>
            <Text style={styles.lastUpdated}>Last Updated: December 11, 2024</Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Introduction</Text>
            <Text style={styles.paragraph}>
              Welcome to HoneyDone ("we," "our," or "us"). We are committed to protecting your privacy and personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our home 
              maintenance management platform, including our website and mobile application (collectively, the "Service").
            </Text>
            <Text style={styles.paragraph}>
              By using HoneyDone, you agree to the collection and use of information in accordance with this Privacy Policy. 
              If you do not agree with our policies and practices, please do not use our Service.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Information We Collect</Text>
            
            <Text style={styles.subsectionTitle}>2.1 Personal Information</Text>
            <Text style={styles.paragraph}>
              When you register for an account or use our Service, we may collect the following personal information:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Full name</Text>
              <Text style={styles.listItem}>• Email address</Text>
              <Text style={styles.listItem}>• Phone number</Text>
              <Text style={styles.listItem}>• Password (encrypted)</Text>
              <Text style={styles.listItem}>• Profile photo (optional)</Text>
            </View>

            <Text style={styles.subsectionTitle}>2.2 Property Information</Text>
            <Text style={styles.paragraph}>
              To provide personalized maintenance plans and services, we collect:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Property address (street, city, state, ZIP code)</Text>
              <Text style={styles.listItem}>• Property details (square footage, year built)</Text>
              <Text style={styles.listItem}>• Home notes and documentation</Text>
              <Text style={styles.listItem}>• Photos and attachments related to your property</Text>
            </View>

            <Text style={styles.subsectionTitle}>2.3 Service and Usage Information</Text>
            <Text style={styles.paragraph}>We collect information about how you use our Service, including:</Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Service requests and their status</Text>
              <Text style={styles.listItem}>• Maintenance tasks and completion records</Text>
              <Text style={styles.listItem}>• Service provider information and interactions</Text>
              <Text style={styles.listItem}>• Expenses, quotes, and invoices</Text>
              <Text style={styles.listItem}>• Visit schedules and history</Text>
              <Text style={styles.listItem}>• Service preferences and interests</Text>
              <Text style={styles.listItem}>• Communication history with service providers</Text>
            </View>

            <Text style={styles.subsectionTitle}>2.4 Payment Information</Text>
            <Text style={styles.paragraph}>
              Payment processing is handled by Stripe, our third-party payment processor. We do not store your complete 
              credit card information on our servers. Stripe collects and processes payment information in accordance with 
              their privacy policy.
            </Text>

            <Text style={styles.subsectionTitle}>2.5 Automatically Collected Information</Text>
            <Text style={styles.paragraph}>
              When you access our Service, we automatically collect certain information:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Device information (type, operating system, browser)</Text>
              <Text style={styles.listItem}>• IP address and location data</Text>
              <Text style={styles.listItem}>• Usage data and analytics</Text>
              <Text style={styles.listItem}>• Log files and error reports</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>3. SMS and Phone Communications</Text>
            
            <Text style={styles.subsectionTitle}>3.1 SMS Messaging</Text>
            <Text style={styles.paragraph}>
              We use a third-party service provider to send SMS text messages to your mobile phone number for:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Account verification and authentication</Text>
              <Text style={styles.listItem}>• Service appointment reminders</Text>
              <Text style={styles.listItem}>• Important updates about your service requests</Text>
              <Text style={styles.listItem}>• Maintenance task reminders</Text>
              <Text style={styles.listItem}>• Critical account notifications</Text>
            </View>

            <Text style={styles.subsectionTitle}>3.2 Consent and Opt-Out</Text>
            <Text style={styles.paragraph}>
              By providing your phone number, you consent to receive SMS messages from HoneyDone. You may opt out 
              of SMS communications at any time by:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Replying "STOP" to any SMS message</Text>
              <Text style={styles.listItem}>• Updating your notification preferences in your account settings</Text>
              <Text style={styles.listItem}>• Contacting us at hello@honeydone.com</Text>
            </View>

            <Text style={styles.subsectionTitle}>3.3 SMS Data Privacy</Text>
            <Text style={styles.paragraph}>
              The SMS provider processes your phone number and message content on our behalf. The SMS provider is committed to protecting your 
              privacy and complies with applicable privacy laws. Your phone number and SMS messages are:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Used only for the purposes described in this policy</Text>
              <Text style={styles.listItem}>• Not sold or shared with third parties for marketing purposes</Text>
              <Text style={styles.listItem}>• Protected with industry-standard security measures</Text>
              <Text style={styles.listItem}>• Retained only as long as necessary to provide our Service</Text>
            </View>

            <Text style={styles.subsectionTitle}>3.4 Message and Data Rates</Text>
            <Text style={styles.paragraph}>
              Standard message and data rates may apply for SMS messages sent to your mobile device. Please check with your 
              mobile carrier for details about your messaging plan.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>4. How We Use Your Information</Text>
            <Text style={styles.paragraph}>We use the information we collect to:</Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Create and manage your account</Text>
              <Text style={styles.listItem}>• Provide and improve our Service</Text>
              <Text style={styles.listItem}>• Generate personalized maintenance plans</Text>
              <Text style={styles.listItem}>• Process service requests and coordinate with service providers</Text>
              <Text style={styles.listItem}>• Send you notifications, reminders, and updates</Text>
              <Text style={styles.listItem}>• Process payments and manage billing</Text>
              <Text style={styles.listItem}>• Provide customer support</Text>
              <Text style={styles.listItem}>• Analyze usage patterns and improve user experience</Text>
              <Text style={styles.listItem}>• Detect and prevent fraud or security issues</Text>
              <Text style={styles.listItem}>• Comply with legal obligations</Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>5. Information Sharing and Disclosure</Text>
            
            <Text style={styles.subsectionTitle}>5.1 Service Providers</Text>
            <Text style={styles.paragraph}>
              We share your information with third-party service providers who perform services on our behalf.
              These service providers are contractually obligated to protect your information and use it only for the 
              purposes we specify.
            </Text>

            <Text style={styles.subsectionTitle}>5.2 Home Service Providers</Text>
            <Text style={styles.paragraph}>
              When you request services, we share necessary information (name, contact information, property address, 
              service details) with home service providers to fulfill your service requests.
            </Text>

            <Text style={styles.subsectionTitle}>5.3 Legal Requirements</Text>
            <Text style={styles.paragraph}>
              We may disclose your information if required to do so by law or in response to valid requests by public 
              authorities (e.g., court orders, subpoenas, government investigations).
            </Text>

            <Text style={styles.subsectionTitle}>5.4 Business Transfers</Text>
            <Text style={styles.paragraph}>
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the 
              acquiring entity. We will notify you via email and/or prominent notice on our Service before your information 
              becomes subject to a different privacy policy.
            </Text>

            <Text style={styles.subsectionTitle}>5.5 With Your Consent</Text>
            <Text style={styles.paragraph}>
              We may share your information for any other purpose with your consent.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>6. Data Security</Text>
            <Text style={styles.paragraph}>
              We implement appropriate technical and organizational security measures to protect your personal information:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Data encryption in transit (SSL/TLS)</Text>
              <Text style={styles.listItem}>• Encrypted password storage</Text>
              <Text style={styles.listItem}>• Row-level security policies in our database</Text>
              <Text style={styles.listItem}>• Regular security audits and updates</Text>
              <Text style={styles.listItem}>• Access controls and authentication</Text>
              <Text style={styles.listItem}>• Secure cloud infrastructure</Text>
            </View>
            <Text style={styles.paragraph}>
              However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive 
              to protect your information, we cannot guarantee absolute security.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>7. Data Retention</Text>
            <Text style={styles.paragraph}>
              We retain your personal information for as long as necessary to:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Provide our Service to you</Text>
              <Text style={styles.listItem}>• Comply with legal obligations</Text>
              <Text style={styles.listItem}>• Resolve disputes and enforce our agreements</Text>
              <Text style={styles.listItem}>• Maintain accurate business records</Text>
            </View>
            <Text style={styles.paragraph}>
              When you delete your account, we will delete or anonymize your personal information within 90 days, except 
              where we are required to retain it for legal or regulatory purposes.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>8. Your Privacy Rights</Text>
            <Text style={styles.paragraph}>Depending on your location, you may have the following rights:</Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Access: Request a copy of your personal information</Text>
              <Text style={styles.listItem}>• Correction: Request corrections to inaccurate information</Text>
              <Text style={styles.listItem}>• Deletion: Request deletion of your personal information</Text>
              <Text style={styles.listItem}>• Data Portability: Request your data in a machine-readable format</Text>
              <Text style={styles.listItem}>• Opt-Out: Unsubscribe from marketing communications</Text>
              <Text style={styles.listItem}>• Withdraw Consent: Withdraw consent for data processing</Text>
            </View>
            <Text style={styles.paragraph}>
              To exercise any of these rights, please contact us at privacy@honeydone.com. We will respond to your 
              request within 30 days.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>9. California Privacy Rights</Text>
            <Text style={styles.paragraph}>
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Right to know what personal information is collected</Text>
              <Text style={styles.listItem}>• Right to know whether your information is sold or disclosed</Text>
              <Text style={styles.listItem}>• Right to say no to the sale of personal information</Text>
              <Text style={styles.listItem}>• Right to access your personal information</Text>
              <Text style={styles.listItem}>• Right to equal service and price</Text>
            </View>
            <Text style={styles.paragraph}>
              We do not sell your personal information to third parties.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>10. Children's Privacy</Text>
            <Text style={styles.paragraph}>
              Our Service is not intended for children under the age of 18. We do not knowingly collect personal 
              information from children under 18. If you are a parent or guardian and believe your child has provided 
              us with personal information, please contact us, and we will delete such information.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>11. Third-Party Links</Text>
            <Text style={styles.paragraph}>
              Our Service may contain links to third-party websites and services. We are not responsible for the 
              privacy practices of these third parties. We encourage you to read their privacy policies before 
              providing any information to them.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>12. International Data Transfers</Text>
            <Text style={styles.paragraph}>
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have different data protection laws. By using our Service, you consent to the 
              transfer of your information to the United States and other countries where we or our service providers 
              operate.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>13. Changes to This Privacy Policy</Text>
            <Text style={styles.paragraph}>
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal 
              requirements. We will notify you of any material changes by:
            </Text>
            <View style={styles.list}>
              <Text style={styles.listItem}>• Posting the new Privacy Policy on this page</Text>
              <Text style={styles.listItem}>• Updating the "Last Updated" date</Text>
              <Text style={styles.listItem}>• Sending you an email notification (for significant changes)</Text>
            </View>
            <Text style={styles.paragraph}>
              Your continued use of our Service after changes are posted constitutes your acceptance of the updated 
              Privacy Policy.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>14. Contact Us</Text>
            <Text style={styles.paragraph}>
              If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, 
              please contact us:
            </Text>
            <View style={styles.contactInfo}>
              <Text style={styles.paragraph}>HoneyDone by Dunzo Group Incorporated</Text>
              <Text style={styles.paragraph}>Support: hello@honeydone.com</Text>
            </View>
          </View>

          <View style={styles.footer}>
            <Link to="/dashboard" style={styles.link}>
              <Text style={styles.linkText}>← Back to Dashboard</Text>
            </Link>
          </View>
        </View>
      </WebScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    maxWidth: 896,
    marginHorizontal: 'auto' as any,
    paddingHorizontal: 16,
    paddingVertical: 48,
    ...(Platform.OS === 'web' && {
      paddingHorizontal: 24,
    }),
  },
  header: {
    marginBottom: 48,
    paddingBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 8,
  },
  lastUpdated: {
    fontSize: 14,
    color: '#6b7280',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 16,
  },
  subsectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 12,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 24,
    color: '#4b5563',
    marginBottom: 12,
  },
  list: {
    marginLeft: 16,
    marginBottom: 12,
  },
  listItem: {
    fontSize: 16,
    lineHeight: 24,
    color: '#4b5563',
    marginBottom: 8,
  },
  contactInfo: {
    backgroundColor: '#f3f4f6',
    padding: 20,
    borderRadius: 8,
    marginTop: 16,
  },
  footer: {
    marginTop: 48,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  link: {
    textDecorationLine: 'none',
  },
  linkText: {
    fontSize: 16,
    color: '#000000',
    fontWeight: '500',
    ...(Platform.OS === 'web' && {
      textDecorationLine: 'underline',
    }),
  },
});

export default PrivacyPolicy;
